/* eslint-disable prettier/prettier */
'use client';

import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import { Search, Folder, Check, Plus } from 'lucide-react';
import { useProductsSlice } from '../slice';
import useDebounce from 'utils/hooks/debounce-hook';
import { Category } from 'types';

export default function Categories() {
  const form = useFormContext();
  const [search, setSearch] = React.useState('');
  const [selectedCategories, setSelectedCategories] = React.useState<
    Category[]
  >([]);
  const [showCategoriesList, setShowCategoriesList] = React.useState(true);
  const [newCategories, setNewCategories] = React.useState<string[]>([]);
  const [newCategoryInput, setNewCategoryInput] = React.useState('');
  const searchDebounce = useDebounce(search, 300);

  // Initialize selected categories from form data
  React.useEffect(() => {
    const formCategories = form.getValues('categories') || [];
    if (formCategories.length > 0) {
      setSelectedCategories(formCategories);
    }
  }, [form]);

  // Update form with new categories
  React.useEffect(() => {
    form.setValue('newCategories', newCategories);
  }, [newCategories, form]);

  const { useLazyGetProductsCategoriesQuery } = useProductsSlice();
  const [getCategories, { data: listData, isLoading }] =
    useLazyGetProductsCategoriesQuery();

  const categories = listData?.response?.data || [];

  // Load categories on mount and when search changes
  React.useEffect(() => {
    getCategories({
      page: 1,
      search: searchDebounce,
      includeSubcategories: true,
    });
  }, [searchDebounce, getCategories]);

  // Memoize handlers to prevent unnecessary re-renders
  const handleCategorySelect = React.useCallback(
    (category: Category) => {
      const isSelected = selectedCategories.some(
        cat => cat.category_id === category.category_id,
      );

      if (isSelected) {
        // Remove category
        const updated = selectedCategories.filter(
          cat => cat.category_id !== category.category_id,
        );
        setSelectedCategories(updated);
        form.setValue('categories', updated);
      } else {
        // Add category
        const updated = [...selectedCategories, category];
        setSelectedCategories(updated);
        form.setValue('categories', updated);
        // Clear search and close categories list after selection
        setSearch('');
        setShowCategoriesList(false);
      }
    },
    [selectedCategories, form],
  );

  const isCategorySelected = React.useCallback(
    (categoryId: number) => {
      return selectedCategories.some(cat => cat.category_id === categoryId);
    },
    [selectedCategories],
  );

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      // Show categories list when user starts typing
      if (e.target.value) {
        setShowCategoriesList(true);
      }
    },
    [],
  );

  const handleAddNewCategory = React.useCallback(() => {
    if (newCategoryInput.trim() && !newCategories.includes(newCategoryInput.trim())) {
      const updated = [...newCategories, newCategoryInput.trim()];
      setNewCategories(updated);
      setNewCategoryInput('');
    }
  }, [newCategoryInput, newCategories]);

  const handleRemoveNewCategory = React.useCallback(
    (categoryName: string) => {
      const updated = newCategories.filter(cat => cat !== categoryName);
      setNewCategories(updated);
    },
    [newCategories],
  );

  const handleNewCategoryKeyPress = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddNewCategory();
      }
    },
    [handleAddNewCategory],
  );

  // Memoize selected categories section
  const selectedCategoriesSection = React.useMemo(() => {
    if (selectedCategories.length === 0) return null;

    return (
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Selected Categories ({selectedCategories.length})
        </h3>
        <div className="flex flex-wrap gap-2">
          {selectedCategories.map(category => (
            <Badge
              key={category.category_id}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1"
            >
              <Folder className="w-3 h-3" />
              {category.category_name}
              <button
                onClick={() => handleCategorySelect(category)}
                className="ml-1 hover:text-red-500"
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      </div>
    );
  }, [selectedCategories, handleCategorySelect]);

  // Memoize new categories section
  const newCategoriesSection = React.useMemo(() => {
    if (newCategories.length === 0) return null;

    return (
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          New Categories to Create ({newCategories.length})
        </h3>
        <div className="flex flex-wrap gap-2">
          {newCategories.map(categoryName => (
            <Badge
              key={categoryName}
              variant="outline"
              className="flex items-center gap-1 px-3 py-1 bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-600"
            >
              <Plus className="w-3 h-3 text-green-600 dark:text-green-400" />
              {categoryName}
              <button
                onClick={() => handleRemoveNewCategory(categoryName)}
                className="ml-1 hover:text-red-500"
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      </div>
    );
  }, [newCategories, handleRemoveNewCategory]);

  // Memoize categories list section
  const categoriesListSection = React.useMemo(() => {
    if (!showCategoriesList) return null;

    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
        <div className="p-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {search ? 'Search Results' : 'All Categories'}
          </h3>
        </div>
        <div className="max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              Loading categories...
            </div>
          ) : categories.length > 0 ? (
            categories.map(category => (
              <div
                key={category.category_id}
                className={`p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${isCategorySelected(category.category_id)
                  ? 'bg-blue-50 dark:bg-blue-900/20'
                  : ''
                  }`}
                onClick={() => handleCategorySelect(category)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded flex items-center justify-center flex-shrink-0">
                      <Folder className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                        {category.category_name}
                      </div>
                      {category.category_path && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {category.category_path}
                        </div>
                      )}
                    </div>
                  </div>
                  {isCategorySelected(category.category_id) && (
                    <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="p-4">
              <div className="text-center text-gray-500 dark:text-gray-400 mb-3">
                {search ? (
                  <p>No categories found matching "{search}"</p>
                ) : (
                  <p>No categories available</p>
                )}
              </div>
              {search && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Category not found? Create a new one:
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder={`Category name (default: "${search}")`}
                      value={newCategoryInput || search}
                      onChange={e => setNewCategoryInput(e.target.value)}
                      onKeyPress={handleNewCategoryKeyPress}
                      className="flex-1 h-9 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        const nameToAdd = newCategoryInput || search;
                        if (nameToAdd.trim() && !newCategories.includes(nameToAdd.trim())) {
                          const updated = [...newCategories, nameToAdd.trim()];
                          setNewCategories(updated);
                          setNewCategoryInput('');
                          setSearch('');
                        }
                      }}
                      disabled={newCategories.includes(newCategoryInput || search)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }, [
    showCategoriesList,
    search,
    isLoading,
    categories,
    isCategorySelected,
    handleCategorySelect,
    newCategoryInput,
    newCategories,
    handleNewCategoryKeyPress,
  ]);

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex shrink-0 items-center justify-center">
            <Folder className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Product Categories
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Search and select categories for your product
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Type to search categories..."
            value={search}
            onChange={handleSearchChange}
            className="pl-10 h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg"
          />
        </div>

        {/* Selected Categories */}
        {selectedCategoriesSection}

        {/* New Categories */}
        {newCategoriesSection}

        {/* Categories List */}
        {categoriesListSection}
      </div>
    </div>
  );
}
